<template>
	<div style="display: contents;">
		<template v-if="buttonComponent === 'v-btn'">
			<v-tooltip :top="tooltipPosition === 'top'" :bottom="tooltipPosition === 'bottom'">
				<template v-slot:activator="{ on: tooltip }">
					<v-btn
						v-on="{ ...tooltip }"
						:fab="fab"
						:icon="!fab"
						:small="small"
						:fixed="fixed"
						:bottom="bottom"
						:right="right"
						:dark="dark"
						:color="buttonColor"
						@click="openDialog">
						<v-icon :size="iconSize" :color="iconColor">{{ icon }}</v-icon>
					</v-btn>
				</template>
				<span>{{ tooltipText }}</span>
			</v-tooltip>
		</template>

		<v-list-item
			v-else-if="buttonComponent === 'v-list-item'"
			dense
			:disabled="disabled"
			@click.stop="openDialog"
			ripple>
			<v-list-item-icon>
				<v-icon style="font-size: 18px">{{ icon }}</v-icon>
			</v-list-item-icon>
			<v-list-item-content>
				<v-list-item-title>{{ tooltipText }}</v-list-item-title>
			</v-list-item-content>
		</v-list-item>

		<v-dialog
			v-model="show"
			:max-width="maxWidth"
			persistent
			class="round-card"
			:fullscreen="fullScreen || $vuetify.breakpoint.smAndDown"
			scrollable>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">{{ dialogTitle }}</p>
					<v-spacer></v-spacer>
					<v-btn icon @click.native.stop="fullScreen = !fullScreen">
						<v-icon>fullscreen</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="px-1 pb-1">
					<create
						v-if="show"
						ref="createComponent"
						:model-def="modelDef"
						:default-input="computedDefaultInput"
						:url-append="urlAppend"
						:query-params="queryParams"
						:show-relations="showRelations"
						:show-save="false"
						:model.sync="internalModel"
						:row-class="'ma-0 pa-0'"
						:redirect="redirect"
						:in-dialog="true"
						@update:model="onModelCreated"
						@validation-failed="loading = false"
					></create>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions style="padding-bottom: calc(16px + env(safe-area-inset-bottom));">
					<v-spacer></v-spacer>
					<v-btn text color="info" @click.native.stop="closeDialog">
						Annulla
					</v-btn>
					<v-btn text color="primary" @click.native.stop="save" :loading="loading">
						{{ saveButtonText }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import Create from '@/components/common/Create'

export default {
	name: 'create-dialog-button',
	components: {
		Create
	},
	props: {
		// Button props
		buttonComponent: { type: String, default: 'v-btn' },
		icon: { type: String, default: 'mdi-plus' },
		iconColor: { type: String, default: '' },
		iconSize: { type: [Number, String], default: 24 },
		buttonColor: { type: String, default: '' },
		tooltipText: { type: String, default: 'Crea' },
		tooltipPosition: { type: String, default: 'bottom' },
		fab: { type: Boolean, default: false },
		small: { type: Boolean, default: false },
		fixed: { type: Boolean, default: false },
		bottom: { type: Boolean, default: false },
		right: { type: Boolean, default: false },
		dark: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },

		// Dialog props
		maxWidth: { type: [Number, String], default: 1200 },
		dialogTitle: { type: String, default: 'Creazione' },
		saveButtonText: { type: String, default: 'Crea' },

		// Create component props
		modelDef: { type: String, required: true },
		defaultInput: { type: Object, default: () => ({}) },
		urlAppend: { type: String, default: '' },
		queryParams: { type: String, default: '' },
		showRelations: { type: Boolean, default: true },
		redirect: { type: Object, default: null },

		// Parent model reference (optional)
		parentModel: { type: Object, default: null },
		parentKey: { type: String, default: '' },

		// Event name to emit on update
		updateEvent: { type: String, default: '' },

		// Auto open dialog on mount
		autoOpen: { type: Boolean, default: false }
	},

	data() {
		return {
			show: false,
			fullScreen: false,
			loading: false,
			internalModel: {}
		}
	},

	computed: {
		computedDefaultInput() {
			let input = { ...this.defaultInput }
			if (this.parentModel && this.parentKey) {
				input[this.parentKey] = this.parentModel.id
			}
			return input
		}
	},

	methods: {
		openDialog() {
			this.internalModel = {}
			this.show = true
		},

		closeDialog() {
			this.show = false
			this.internalModel = {}
			this.$emit('close')
		},

		save() {
			if (this.$refs.createComponent && this.$refs.createComponent.$refs.fieldsgenerator) {
				this.loading = true
				this.$refs.createComponent.createModel()
			}
		},

		onModelCreated(model) {
			this.loading = false
			this.show = false
			this.internalModel = {}

			this.$emit('created', model)

			if (this.updateEvent) {
				this.$eventBusUtility.$emit(this.updateEvent)
			}
		}
	},

	watch: {
		show(val) {
			if (!val) {
				this.loading = false
			}
		}
	},

	mounted() {
		// Auto open dialog if prop is set
		if (this.autoOpen) {
			this.openDialog()
		}

		this.$eventBusUtility.$on('modelCreated', (id) => {
			if (this.show) {
				this.loading = false
				this.show = false
				this.internalModel = {}

				this.$emit('created', { id })

				if (this.updateEvent) {
					this.$eventBusUtility.$emit(this.updateEvent)
				}
			}
		})
	},

	beforeDestroy() {
		this.$eventBusUtility.$off('modelCreated')
	}
}
</script>
